// 사용자(공개) 카탈로그 API 호출 래퍼.
// 카테고리/상품/배너 GET 은 비로그인도 호출 가능 (withAuth: false).
// 찜은 사용자 토큰 필요.

import type { BannerDto, CategoryDto, ProductDto, ProductSort, UnitDto } from "@oo/shared";
import { api } from "@/api/client.ts";

export interface ListProductsResult {
  products: ProductDto[];
  total: number;
  nextCursor: string | null;
}

// 노출 카테고리 목록 (비로그인 가능).
export async function listCategories(): Promise<CategoryDto[]> {
  const data = await api.get<{ categories: CategoryDto[] }>("/categories", { withAuth: false });
  return data.categories;
}

// 활성 단위 목록 (비로그인 가능). 상품/주문 단위 라벨·중량환산 표시에 사용.
export async function listUnits(): Promise<UnitDto[]> {
  const data = await api.get<{ units: UnitDto[] }>("/units", { withAuth: false });
  return data.units;
}

// 메인 노출용 인기 상품 목록 (비로그인 가능).
export async function listBestProducts(limit = 5): Promise<ProductDto[]> {
  const data = await api.get<{ products: ProductDto[] }>(`/products/best?limit=${limit}`, {
    withAuth: false,
  });
  return data.products;
}

// 노출 중인 배너 목록 (비로그인 가능).
export async function listVisibleBanners(): Promise<BannerDto[]> {
  const data = await api.get<{ banners: BannerDto[] }>("/banners", { withAuth: false });
  return data.banners;
}

// 상품 목록. 카테고리/검색어/커서 페이지네이션을 쿼리스트링으로 전달 (비로그인 가능).
export async function listProducts(params: {
  categoryId?: string;
  q?: string;
  cursor?: string;
  limit?: number;
  sort?: ProductSort;
  excludeSoldOut?: boolean;
}): Promise<ListProductsResult> {
  const search = new URLSearchParams();
  if (params.categoryId) search.set("categoryId", params.categoryId);
  if (params.q) search.set("q", params.q);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if (params.excludeSoldOut) search.set("excludeSoldOut", "1");
  const qs = search.toString();
  return api.get<ListProductsResult>(`/products${qs ? `?${qs}` : ""}`, { withAuth: false });
}

// 상품 상세 단건 (비로그인 가능).
export async function getProduct(id: string): Promise<ProductDto> {
  const data = await api.get<{ product: ProductDto }>(`/products/${id}`, { withAuth: false });
  return data.product;
}

// 찜 (인증 필수).
export async function listWishlist(): Promise<ProductDto[]> {
  const data = await api.get<{ products: ProductDto[] }>("/wishlist");
  return data.products;
}

// 찜 추가 (인증 필수).
export async function addToWishlist(productId: string): Promise<void> {
  await api.post(`/wishlist/${productId}`);
}

// 찜 해제 (인증 필수).
export async function removeFromWishlist(productId: string): Promise<void> {
  await api.delete(`/wishlist/${productId}`);
}

// 카드 목록의 찜 상태 일괄 조회.
export async function checkWishlist(productIds: string[]): Promise<Record<string, boolean>> {
  if (productIds.length === 0) return {};
  const data = await api.post<{ wishlisted: Record<string, boolean> }>("/wishlist/check", { productIds });
  return data.wishlisted;
}
